import axios from 'axios';
import {
  CreatePropertyFormApi,
  CreatePropertyFormForApi,
  CreateVisitFormApi,
  PropertyAdditionalInfoForm,
  PropertyLocationAddressForm,
  PropertyUpdateIsVerifiedApi,
  PublishPropertyForm,
  RescheduleFormApi,
  UpdatePropertyAmenitiesApiPayload,
  UpdatePropertyRentInfoForm,
  UpdatePropertySalesPricesForm,
  UploadLinksForm,
  UpdatePropertyOperation,
  UpdatePropertyAvailabilityForm
} from '../models/propertyModel';
import { NearbySavePayload } from '../models/nearbyModel';

const loadAbort = () => {
  return new AbortController();
};

const rsUrl = (urlService: string, path: string) => `${urlService}/rs/v1${path}`;

export const getCharacteristicsEndpoint = (token: string = '') => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: rsUrl(urlService, '/characteristics?is_special_feature=true'),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export interface CreateFeatureCharacteristicPayload {
  description: string;
  /** Nombre de un ícono de Tabler, ej. "IconSun" */
  icon?: string;
  feature_group?: 'interior' | 'exterior';
}

/** Agrega una característica extra al catálogo (si el nombre ya existe devuelve esa). */
export const createFeatureCharacteristicEndpoint = (
  payload: CreateFeatureCharacteristicPayload,
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: rsUrl(urlService, '/characteristics/features'),
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export interface CreateAmenityPayload {
  description: string;
  is_enabled: boolean;
  /** Nombre de un ícono de Tabler, ej. "IconPool" */
  icon?: string;
}

/** Agrega una amenidad al catálogo. */
export const createAmenityEndpoint = (
  payload: CreateAmenityPayload,
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: rsUrl(urlService, '/amenities'),
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getTypePropertiesEndpoint = (token: string = '') => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/type-properties`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getPropertyTagsEndpoint = (token: string = '') => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/tags`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getAmenitiesEndpoint = (token: string = '') => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/amenities`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getPlansEndpoint = (token: string = '') => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/plans`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getTenantByMailEndpoint = (email: string, token: string) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/user/v1/admin/tenant/search-mail`,
        params: { email },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getPropertiesEndpoint = (query: string = '', token: string) => {
  const controller = loadAbort();

  const queryString = query
    ? (query.startsWith('?') ? query : `?${query}`)
    : '';
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties${queryString}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getMasterPropertiesEndpoint = (query: string = '', token: string) => {
  const controller = loadAbort();

  const queryString = query
    ? (query.startsWith('?') ? query : `?${query}`)
    : '';
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties-master${queryString}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getPhotoSessionsPropertiesEndpoint = (
  query: string = '',
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties${query}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getPropertyToEditEndpoint = (
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/edit`),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const createPropertyEndpoint = (
  form: CreatePropertyFormForApi,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: `${urlService}/rs/v1/admin/properties`,
        data: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const updatePropertyEndpoint = (
  form:
    | CreatePropertyFormForApi
    | PropertyAdditionalInfoForm
    | PropertyLocationAddressForm
    | UpdatePropertyRentInfoForm
    | UpdatePropertyAmenitiesApiPayload
    | UpdatePropertyOperation
    | UpdatePropertyAvailabilityForm,
  propertyId: string,
  token: string,
) => {

  //console.log('Petición:', form);
  //console.log('token:', token);
  const controller = loadAbort();
  return {
    call: async (urlService: string) => {
      try {
        const response = await axios({
          method: 'put',
          url: rsUrl(urlService, `/admin/properties/${propertyId}`),
          data: JSON.stringify(form),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        //console.log('Respuesta:', response.data);
        return response;
      } catch (error) {
        //console.error('Error en petición:', error);
        throw error;
      }
    },
    controller,
  };
};

export const getAdminPropertyEndpoint = (propertyId: string, token: string) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getAdminPropertyDetailEndpoint = (
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/detail`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getPhotosEndpoint = (propertyId: string, token: string) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/gallery`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const uploadPhotosEndpoint = (
  form: any,
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/upload-images`,
        data: form,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const deleteGalleryPhotoEndpoint = (
  propertyId: string,
  imageUrl: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'delete',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/gallery`,
        data: JSON.stringify({ path: imageUrl }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

//delete all

export const deleteGalleryPropertyEndpoint = (
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'delete',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/gallery-items`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

//sync
export const syncPropertyGalleryEndpoint = (
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/sync-gallery`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

//update gallery
export const uploadPropertyGalleryResourcesEndpoint = (
  propertyId: string,
  form: any,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/gallery`,
        data: form,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

//sort gallery
export const sortPropertyGalleryEndpoint = (
  propertyId: string,
  images: string[],
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/sort-gallery`,
        data: JSON.stringify({ paths: images }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getPropertyVisitsEndpoint = (
  propertyId: string,
  start: string,
  end: string,
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/ts/v1/admin/properties/${propertyId}/visits?start=${start}&end=${end}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const updatePropertyStatusEndpoint = (
  propertyId: string,
  form: PublishPropertyForm,
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}`,
        data: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const createPropertyVisitsEndpoint = (
  form: CreateVisitFormApi,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: `${urlService}/ts/v1/admin/visits`,
        data: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const updatePropertyVisitsEndpoint = (
  form: RescheduleFormApi,
  visitId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/ts/v1/admin/visits/${visitId}/reschedule`,
        data: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getAvailableTimeVisitsEndpoint = (
  propertyId: string,
  day: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/ts/v1/admin/visits/${propertyId}/available-time-visits/${day}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const uploadLinksPropertiesEndpoint = (
  form: UploadLinksForm,
  propertyId: string,
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}`,
        data: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getLandlordPropertyOffers = (
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/ts/v1/admin/properties/${propertyId}/offers`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const takeDownPropertyEndpoint = (
  propertyId: string,
  payload: any,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/hide`,
        data: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const deleteVisitEndpoint = (visitId: string, token: string, reason?: string, explain_reason?: string) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/ts/v1/admin/visits/${visitId}`,
        data: JSON.stringify({
          reason: reason ?? '',
          explain_reason: explain_reason ?? ''
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getCancellationReasons = (
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) => {
      //console.log('getCancellationReasons urlService:', urlService); // <-- imprime la url base
      return axios({
        method: 'get',
        url: `${urlService}/ts/v1/admin/cancellation-reasons`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    controller,
  };
};

export const getPropertyBdrlistSetting = (
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) => {
      return axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/bdrs`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    controller,
  };
};

export const getPropertySourcelistSetting = (
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) => {
      return axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/sources`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    controller,
  };
};

export const getPropertyZonelistSetting = (
  token: string = '',
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) => {
      return axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/zones`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    controller,
  };
};

export const editProperVisitVisitEndpoint = (
  properId: string,
  visitId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/ts/v1/admin/visits/${visitId}/update-proper`,
        data: JSON.stringify({ proper_id: properId }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const getAdminPropertyViewCounterEndpoint = (
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}/views`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const updatePropertyIsVerifiedEndpoint = (
  form: PropertyUpdateIsVerifiedApi,
  propertyId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: `${urlService}/rs/v1/admin/properties/${propertyId}`,
        data: JSON.stringify(form),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};


// Lugares cercanos y ventajas de la zona (rs-service: admin/properties/:id/nearby).
export const getNearbyEndpoint = (propertyId: string, token: string) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/nearby`),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

// Genera con IA (n8n) un borrador; no guarda. Tarda: Gemini + Google Maps.
export const suggestNearbyEndpoint = (
  propertyId: string,
  force: boolean,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/nearby/suggest`),
        params: force ? { force: true } : undefined,
        timeout: 130000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const saveNearbyEndpoint = (
  propertyId: string,
  payload: NearbySavePayload,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/nearby`),
        data: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

export const uploadNearbyImageEndpoint = (
  propertyId: string,
  form: FormData,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/nearby/image`),
        data: form,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};


// Estimate de precio guardado (rs-service: admin/properties/:id/estimate).
export const getEstimateEndpoint = (propertyId: string, token: string) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/estimate`),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }),
    controller,
  };
};

export const recomputeEstimateEndpoint = (
  propertyId: string,
  market: 'rent' | 'sale',
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/estimate/recompute`),
        params: { market },
        timeout: 60000,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }),
    controller,
  };
};

export const saveEstimateAnalysisEndpoint = (
  propertyId: string,
  market: 'rent' | 'sale',
  text: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'put',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/estimate/analysis`),
        data: JSON.stringify({ market, text }),
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }),
    controller,
  };
};

// Regenera con IA el análisis del estimate (tarda ~15 s; reemplaza uno escrito a mano).
export const generateEstimateAnalysisEndpoint = (
  propertyId: string,
  market: 'rent' | 'sale',
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: rsUrl(urlService, `/admin/properties/${propertyId}/estimate/analysis/generate`),
        params: { market },
        timeout: 130000,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }),
    controller,
  };
};
